import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  Clock,
  FileText,
  Users,
  Book,
  Bookmark,
  UserPlus,
  LogOut,
  HeartHandshake,
  Calendar,
  GraduationCap,
  Shield
} from "lucide-react";
import EmployeeBulletinBoard from "./components/EmployeeBulletinBoard";

const PortalLink = ({ icon: Icon, title, description, to, isExternal = false }) => {
  const LinkComponent = isExternal ? 'a' : Link;
  const linkProps = isExternal ? { href: to, target: "_blank", rel: "noopener noreferrer" } : { to };

  return (
    <LinkComponent
      {...linkProps}
      className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500"
    >
      <div className="flex items-center mb-4">
        <div className="bg-orange-100 p-2 rounded-full mr-3">
          <Icon className="h-6 w-6 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-green-800">{title}</h2>
      </div>
      <p className="text-gray-600">{description}</p>
    </LinkComponent>
  );
};

const EmployeePortal = () => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Employee Portal</h1>
      
      {/* Bulletin Board Section */}
      <div className="mb-8">
        <EmployeeBulletinBoard maxItems={5} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <PortalLink
          icon={Clock}
          title="Timesheet"
          description="Submit and manage your work hours"
          to="https://sbg-attendance.com/"
          isExternal
        />

        <PortalLink
          icon={FileText}
          title="Documents"
          description="Access onboarding and company documents"
          to="/employee/documents"
        />

        <PortalLink
          icon={Book}
          title="Employee Manual"
          description="Review and sign the employee manual through Adobe Sign"
          to="/employee/manual"
        />

        <PortalLink
          icon={FileText}
          title="W2 Documents"
          description="Access your W2 tax documents"
          to="/employee/w2"
        />

        <PortalLink
          icon={HeartHandshake}
          title="Employee Relations"
          description="Support, guidance, and conflict resolution"
          to="/employee-relations"
        />

        <PortalLink
          icon={Calendar}
          title="Holidays & PTO"
          description="View holiday schedule and PTO policies"
          to="/holidays-and-pto"
        />

        <PortalLink
          icon={Users}
          title="Directory"
          description="Phone: 602-400-8028 | Fax: 623-321-1327"
          to="/directory"
        />

        <PortalLink
          icon={Briefcase}
          title="Job Openings"
          description="View internal job postings and opportunities"
          to="/employee-jobs"
        />

        <PortalLink
          icon={GraduationCap}
          title="Training"
          description="Access training materials and onboarding resources."
          to="/employee/training"
        />

        <PortalLink
          icon={UserPlus}
          title="Employee Referrals"
          description="Refer qualified candidates and earn rewards"
          to="/referrals"
        />

        <PortalLink
          icon={Bookmark}
          title="Saved Jobs"
          description="View and manage your saved job listings"
          to="/employee/saved-jobs"
        />

        <PortalLink
          icon={LogOut}
          title={t('employeePortal.links.resignation.title')}
          description={t('employeePortal.links.resignation.description')}
          to="/employee/resignation"
        />

        {/* Required Workplace Notices */}
        <PortalLink
          icon={Shield}
          title="Workers' Compensation Notice"
          description="Official Bilingual Notice (English/Español) - Arizona Workers' Compensation Law"
          to="/employee/workers-comp-notice"
        />

        <PortalLink
          icon={Shield}
          title="USERRA Rights Poster"
          description="Official notice of rights for uniformed service members and applicants"
          to="/employee/notices/userra-poster"
        />

        {/* Safety Posters Group */}
        <PortalLink
          icon={Shield}
          title="Workplace Safety (English)"
          description="Scanned ADOSH Employee Safety and Health Protection poster"
          to="/employee/notices/employee-safety-health-english"
        />

        <PortalLink
          icon={Shield}
          title="Protección de Seguridad (Español)"
          description="Cartel escaneado de ADOSH sobre seguridad y sanidad para el empleado"
          to="/employee/notices/employee-safety-health-spanish"
        />

        {/* Exposure Notices Group */}
        <PortalLink
          icon={Shield}
          title="MRSA & TB Exposure (English)"
          description="Scanned notice for MRSA, spinal meningitis, and TB exposure"
          to="/employee/notices/mrsa-meningitis-tb-exposure-english"
        />

        <PortalLink
          icon={Shield}
          title="Bodily Fluids Exposure (English)"
          description="Scanned notice for HIV, AIDS, and Hepatitis C exposure"
          to="/employee/notices/bodily-fluids-exposure-english"
        />

        <PortalLink
          icon={Shield}
          title="Exposición a Fluidos (Español)"
          description="Aviso escaneado sobre fluidos corporales, VIH, SIDA y Hepatitis C"
          to="/employee/notices/bodily-fluids-exposure-spanish"
        />
      </div>
    </div>
  );
};

export default EmployeePortal;
